import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export const FORMS = ["Tab", "Cap", "Syp", "Susp", "Inj", "Drops", "Oint", "Cream", "Gel", "Lotion", "Inhaler", "Rotacap", "Nasal spray", "Sachet", "Powder", "Patch", "Suppository", "Mouthwash", "Soap"];
export const FREQS = ["Once daily (OD)", "Twice daily (BD)", "Thrice daily (TDS)", "Four times daily (QID)", "At bedtime (HS)", "When required (SOS)", "Immediately (STAT)", "Alternate days", "Once weekly"];
export const FOODS = ["After food", "Before food", "Empty stomach", "With food", "At bedtime"];
export const ROUTES = ["Oral", "Sublingual", "Transdermal", "Subcutaneous", "Intramuscular", "Intravenous", "Suppository (rectal)", "Topical", "Inhalation", "Nasal", "Ophthalmic", "Otic", "Vaginal"];

function Dropdown({ open, children }) {
  if (!open) return null;
  return <div className="absolute z-50 mt-1 w-full min-w-[220px] max-h-64 overflow-auto rounded-xl border border-[#E2E8ED] bg-white shadow-lg py-1">{children}</div>;
}

const Opt = ({ onPick, children, testId }) => (
  <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(); }} className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-[#F7F9FA]" data-testid={testId}>{children}</button>
);

export function ComboInput({ value, onChange, options, placeholder, disabled, testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").toLowerCase();
  const list = options.filter((o) => !q || o.toLowerCase().includes(q) || options.includes(value));
  const custom = value && !options.some((o) => o.toLowerCase() === q);
  return (
    <div className={`relative ${className}`}>
      <Input value={value || ""} placeholder={placeholder} disabled={disabled} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} data-testid={testId} />
      <Dropdown open={open && !disabled}>
        {list.map((o) => <Opt key={o} onPick={() => { onChange(o); setOpen(false); }} testId={`${testId}-opt-${o.split(" ")[0].toLowerCase()}`}>{o}</Opt>)}
        {custom ? <Opt onPick={() => setOpen(false)} testId={`${testId}-custom`}><Plus className="h-4 w-4 mt-0.5 text-[#086B2D]" />Use custom: "{value}"</Opt>
          : <p className="px-3 py-2 text-xs muted">Type to add a custom option</p>}
      </Dropdown>
    </div>
  );
}

export function MedicineInput({ value, onPick, onChange, disabled, testId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const typed = useRef(false);
  useEffect(() => {
    if (!typed.current || (value || "").trim().length < 3) { setItems([]); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.post("/rx-ai/medicines", { q: value.trim() }); setItems(data.items); } catch { setItems([]); } finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);
  const pick = (i) => { typed.current = false; setOpen(false); onPick(i); };
  return (
    <div className="relative">
      <Input value={value || ""} placeholder="Medicine – type generic or brand" disabled={disabled} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        onChange={(e) => { typed.current = true; onChange(e.target.value); setOpen(true); }} data-testid={testId} autoComplete="off" />
      <Dropdown open={open && !disabled && typed.current && (value || "").length >= 3}>
        <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#086477] flex items-center gap-1"><Sparkles className="h-3 w-3" />AI suggestions · verify before prescribing</p>
        {busy && items.length === 0 && <p className="px-3 py-2 text-sm muted flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Finding generics & Indian brands…</p>}
        {items.map((i, n) => (
          <Opt key={n} onPick={() => pick(i)} testId={`${testId}-sugg-${n}`}>
            {i.brand ? <span className="flex-1"><b>{i.brand}</b> <span className="muted">({i.generic})</span> {i.strength && <span className="text-xs muted">{i.form} {i.strength}</span>}</span>
              : <span className="flex-1"><span className="rounded bg-[#086B2D]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#086B2D] mr-1">GENERIC</span><b>{i.generic}</b></span>}
            {i.company && <span className="text-xs text-[#2412B5] whitespace-nowrap">{i.company}</span>}
          </Opt>))}
        {!busy && items.length === 0 && <p className="px-3 py-2 text-xs muted">No suggestions – your typed name will be used.</p>}
      </Dropdown>
    </div>
  );
}
